import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { ReportView } from '../components/ReportView.js';
function extractDomain(input) {
    let cleaned = input.trim().toLowerCase();
    if (cleaned.includes('://') || /^(https?|ftp):/.test(cleaned)) {
        try {
            cleaned = new URL(cleaned).hostname;
        }
        catch {
            cleaned = cleaned.replace(/^[a-z]+:\/\//, '').split(/[/:?#]/)[0];
        }
    }
    else {
        cleaned = cleaned.split(/[/:?#]/)[0];
    }
    return cleaned.replace(/\.$/, '');
}
export function CheckPage() {
    const { domain: rawDomain } = useParams();
    const domain = rawDomain ? extractDomain(rawDomain) : undefined;
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    const jsonMode = mode === 'json';
    const jsonRawMode = mode === 'jsonraw';
    const [jobId, setJobId] = useState(null);
    const [job, setJob] = useState(null);
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const submitted = useRef(false);
    // Auto-submit scan on mount
    useEffect(() => {
        if (!domain || submitted.current)
            return;
        submitted.current = true;
        (async () => {
            try {
                const result = await api.submitScan(domain);
                setJobId(result.data.job_id);
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to start scan');
            }
        })();
    }, [domain]);
    // Poll for status
    useEffect(() => {
        if (!jobId)
            return;
        let interval;
        async function poll() {
            try {
                const result = await api.getScanStatus(jobId);
                setJob(result.data);
                if (result.data.status === 'completed') {
                    clearInterval(interval);
                    const rep = await api.getReport(jobId);
                    setReport(rep.data);
                }
                else if (result.data.status === 'failed') {
                    clearInterval(interval);
                    setError(result.data.error_message || 'Scan failed');
                }
            }
            catch {
                clearInterval(interval);
                setError('Failed to fetch scan status');
            }
        }
        poll();
        interval = setInterval(poll, 2000);
        return () => clearInterval(interval);
    }, [jobId]);
    if (error) {
        return (_jsx("main", { className: "max-w-5xl mx-auto px-4 py-20 text-center", children: _jsx("p", { className: "text-danger", children: error }) }));
    }
    // jsonRawMode is handled server-side by Pages Function — returns raw JSON before SPA loads
    if (report && jsonMode) {
        const jsonStr = JSON.stringify(report, null, 2);
        return (_jsxs("main", { className: "max-w-7xl mx-auto px-4 py-8", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("h2", { className: "text-lg font-semibold text-white", children: ["JSON Report \u2014 ", domain] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => { navigator.clipboard.writeText(jsonStr); }, className: "px-3 py-1.5 bg-brand/20 text-brand rounded text-sm hover:bg-brand/30 transition", children: "Copy JSON" }), _jsx("button", { onClick: () => {
                                        const blob = new Blob([jsonStr], { type: 'application/json' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `dntools-${domain}.json`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }, className: "px-3 py-1.5 bg-brand/20 text-brand rounded text-sm hover:bg-brand/30 transition", children: "Download .json" }), _jsx("a", { href: `/check/${domain}`, className: "px-3 py-1.5 bg-surface-200 text-surface-600 rounded text-sm hover:text-white transition", children: "View Report" })] })] }), _jsx("pre", { className: "bg-card border border-card-border rounded-lg p-4 text-xs font-mono text-surface-700 overflow-auto max-h-[85vh] whitespace-pre-wrap break-words", children: jsonStr })] }));
    }
    if (report) {
        return _jsx(ReportView, { data: report });
    }
    // Loading state
    return (_jsx("main", { className: "max-w-3xl mx-auto px-4 py-24", children: _jsxs("div", { className: "card text-center", children: [_jsxs("svg", { className: "animate-spin w-8 h-8 text-brand mx-auto mb-4", viewBox: "0 0 24 24", fill: "none", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" })] }), _jsxs("h2", { className: "text-lg font-semibold text-white mb-2", children: ["Analyzing ", domain, "..."] }), job && (_jsxs(_Fragment, { children: [_jsx("div", { className: "w-full max-w-xs mx-auto bg-surface-200 rounded-full h-1.5 mb-2", children: _jsx("div", { className: "bg-brand h-1.5 rounded-full transition-all duration-500", style: { width: `${job.progress}%` } }) }), _jsx("p", { className: "text-surface-600 text-sm", children: job.progress_message || 'Processing...' })] }))] }) }));
}
